import { supabase } from './supabase';

// Suggestion only — the caller must show these as editable, unconfirmed
// fields (never auto-save). See docs/AI_RULES.md: never invent medical
// data; when unsure, leave a field empty rather than guessing.
export interface MedicineDescription {
  activeIngredient?: string;
  dosage?: string;
  usageTags?: string[];
  usageSummary?: string;
  // How the dosage form is taken (swallow whole/chew/dissolve/let dissolve
  // under the tongue) -- never a dosing schedule (frequency/max dose/timing
  // vs food); the server prompt is instructed to never return that.
  intakeMethod?: string;
}

// The actual Anthropic call happens server-side in
// supabase/functions/describe-medicine — the API key is a Supabase Edge
// Function secret and never reaches the browser. This requires a signed-in
// session (the function's verify_jwt is on).
export async function describeMedicine(name: string, model: string, dosage?: string): Promise<MedicineDescription> {
  if (!supabase) throw new Error('Supabase не настроен');
  const { data, error } = await supabase.functions.invoke('describe-medicine', {
    body: { name, model, dosage },
  });
  if (error) {
    let message = error.message;
    if ('context' in error && error.context instanceof Response) {
      try {
        const body = await error.context.clone().json();
        if (body?.error) message = body.error;
      } catch {
        // ignore — fall back to the generic error.message
      }
    }
    throw new Error(message);
  }
  return data as MedicineDescription;
}

// Dosing schedule (frequency/max dose/food timing) is deliberately NOT part
// of MedicineDescription above — describe-medicine never returns it, by
// policy (docs/AI_RULES.md, "Medicine dosing schedule"). This is instead a
// grounded web-search lookup: the server only returns text that came from an
// actual official page it found and cites, never from the model's own
// knowledge. `found: false` means no citable official source was located —
// show that as "not found", not as an error.
export type MedicineDosingResult =
  | { found: false }
  | { found: true; dosingScheduleText: string; sourceName?: string; sourceUrl: string };

export async function findMedicineDosing(
  name: string,
  activeIngredient: string,
  dosage: string,
  model: string,
): Promise<MedicineDosingResult> {
  if (!supabase) throw new Error('Supabase не настроен');
  const { data, error } = await supabase.functions.invoke('find-medicine-dosing', {
    body: { name, activeIngredient, dosage, model },
  });
  if (error) {
    let message = error.message;
    if ('context' in error && error.context instanceof Response) {
      try {
        const body = await error.context.clone().json();
        if (body?.error) message = body.error;
      } catch {
        // ignore — fall back to the generic error.message
      }
    }
    throw new Error(message);
  }
  return data as MedicineDosingResult;
}
